// Discount-from-NSP maths for the Suggested Price split-cell editor.

export const MIN_PRICE = 0;

const PERCENT_SCALE = 100;

const isUsableNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

/**
 * Rounds to a fixed number of decimal places, returning a number rather than a string.
 *
 * `toFixed` is used rather than a `Math.round(v * 10 ** p) / 10 ** p` construction because the
 * latter reintroduces float error at larger magnitudes. It does NOT make rounding exact: a value
 * that is already unrepresentable in binary floating point rounds by its stored value, not its
 * literal. `roundToPrecision(1.005, 2)` is 1, because 1.005 is stored as 1.00499...; this matches
 * `toFixed` everywhere else in the app and is left alone deliberately rather than papered over
 * with an epsilon nudge, which would trade this edge for a less predictable one.
 */
export const roundToPrecision = (value: number, precision: number): number => (
  Number(value.toFixed(Math.max(0, precision)))
);

/**
 * The Suggested Price expressed as a discount off the row's Negotiation Starting Price.
 *
 * This is a discount, not a proportion: NSP 100 / price 75 is 25%, and the sign convention matches
 * the `TrendIndicator` the display renderer already shows under the cell. A price ABOVE NSP is a
 * premium and yields a NEGATIVE discount (NSP 100 / price 125 is -25%), which is deliberate —
 * flooring it at 0 would make premiums unrepresentable even though the column supports them.
 *
 * `max_discount` (`ui_config.anchor_prices.max_discount`) is intentionally not applied: that ceiling
 * governs AI-generated predictive prices only, and a buyer overriding the cell by hand is not bound
 * by it. The only bound is that the PRICE cannot go below zero, which implicitly caps the discount
 * at 100%.
 *
 * Returns `null` when the discount is not derivable, which the editor surfaces by disabling the
 * percent input rather than showing a misleading `0`:
 *   - NSP missing/non-numeric — should not happen in practice, but the column can be absent from
 *     the grid depending on flag combinations, so it is handled rather than assumed.
 *   - price missing/non-numeric — an empty cell has no discount.
 *
 * NSP of exactly 0 is a defined case, not a missing one: there is no baseline to discount from, so
 * it returns 0 (and dividing by it is avoided).
 */
export const computeDiscountPercent = (
  nsp: number | null | undefined,
  price: number | null | undefined,
  precision: number,
): number | null => {
  if (!isUsableNumber(nsp)) return null;
  if (nsp === 0) return 0;
  if (!isUsableNumber(price)) return null;

  const discount = ((nsp - price) / nsp) * PERCENT_SCALE;

  return roundToPrecision(discount, precision);
};

/**
 * Inverse of `computeDiscountPercent` — the dollar price a given discount off NSP works out to.
 *
 * Floors at zero: a discount over 100% would imply a negative price, so it clamps to $0 rather than
 * committing a negative override. Negative discounts (premiums) pass through and raise the price.
 *
 * Returns `null` on the same non-derivable inputs, so the caller leaves the dollar field alone
 * instead of overwriting it with a bogus 0.
 */
export const computePriceFromDiscountPercent = (
  nsp: number | null | undefined,
  percent: number | null | undefined,
  precision: number,
): number | null => {
  if (!isUsableNumber(nsp)) return null;
  if (!isUsableNumber(percent)) return null;

  const price = nsp * (1 - percent / PERCENT_SCALE);

  return roundToPrecision(Math.max(MIN_PRICE, price), precision);
};

/**
 * Parses what a user typed into either field.
 *
 * Returns `null` for blank or unparseable input so callers can distinguish "cleared the field"
 * from "typed a zero" — the two commit differently.
 *
 * The regex strips everything that is not a digit, a decimal point, or a minus sign — so grouping
 * separators and any currency/percent decoration the user may have pasted in are removed. The
 * explicit rejection after that matters: `Number('')` is 0, so a fully non-numeric entry such as
 * "abc" would otherwise parse as a real zero and commit a $0 override. Anything reducing to
 * nothing, or to a lone sign or separator, is unparseable rather than zero.
 */
export const parseEditorInput = (raw: string): number | null => {
  const trimmed = raw.trim();

  if (trimmed === '') return null;

  const stripped = trimmed.replace(/[^\d.-]/g, '');

  if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return null;

  const normalized = Number(stripped);

  return Number.isFinite(normalized) ? normalized : null;
};
